import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Container, Image, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

import api from "../../api/http";

// ✅ Refolosește layout/card styles din CarsManager
import "../../styles/admin/CarManager.css";
import "../../styles/admin/CarAdd.css";

// ✅ Doar modalul pentru CommentsManager (nume clase diferite)
import "../../styles/admin/Modal-coments-rating.css";

import DeleteComments from "./DeleteComments";

const PRODUCTS_PER_PAGE = 12;

const CommentsManager = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // ✅ pagination state
  const [count, setCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const parseApiError = useCallback((err) => {
    const data = err?.response?.data;
    if (!data) return err?.message || "A apărut o eroare.";
    if (typeof data === "string") return data;
    if (Array.isArray(data)) return data[0] || "A apărut o eroare.";
    if (typeof data?.detail === "string") return data.detail;

    if (typeof data === "object") {
      const k = Object.keys(data)[0];
      const v = data[k];
      if (Array.isArray(v)) return v[0];
      if (typeof v === "string") return v;
      return JSON.stringify(data);
    }

    return "A apărut o eroare.";
  }, []);

  const makeImageUrl = useCallback((src) => {
    if (!src || typeof src !== "string") return "";
    if (src.startsWith("http://") || src.startsWith("https://")) return src;

    const base = api?.defaults?.baseURL || "";
    if (!base) return src;

    if (base.endsWith("/") && src.startsWith("/")) return base.slice(0, -1) + src;
    if (!base.endsWith("/") && !src.startsWith("/")) return base + "/" + src;
    return base + src;
  }, []);

  const handleCloseModal = useCallback(() => setSelectedProduct(null), []);

  // ESC close + lock scroll
  useEffect(() => {
    if (!selectedProduct) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") handleCloseModal();
    };

    document.addEventListener("keydown", onKeyDown);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [selectedProduct, handleCloseModal]);

  // =========================
  // ✅ Pagination helpers
  // =========================
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(count / PRODUCTS_PER_PAGE)),
    [count]
  );

  const pageNumbers = useMemo(
    () => Array.from({ length: totalPages }, (_, i) => i + 1),
    [totalPages]
  );

  // =========================
  // ✅ Fetch paginated products
  // =========================
  const fetchProducts = useCallback(
    async (signal, page = currentPage) => {
      setLoading(true);
      setError("");
      try {
        const res = await api.get("/produs/car", {
          signal,
          params: { page, page_size: PRODUCTS_PER_PAGE },
        });

        const data = res.data;

        const list = Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data)
          ? data
          : [];

        if (!mountedRef.current) return;

        setProducts(list);
        setCount(Number(data?.count ?? list.length));
      } catch (err) {
        if (mountedRef.current) setError(parseApiError(err));
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [parseApiError, currentPage]
  );

  // load on page change
  useEffect(() => {
    const controller = new AbortController();
    fetchProducts(controller.signal, currentPage);
    return () => controller.abort();
  }, [fetchProducts, currentPage]);

  // Nu mai sortăm local (backend deja dă pagina). Dacă vrei sortare pe pagină:
  const productsSorted = useMemo(() => {
    return [...products].sort((a, b) => (b?.id ?? 0) - (a?.id ?? 0));
  }, [products]);

  const openProduct = useCallback((product) => setSelectedProduct(product), []);

  // când ștergi comentarii, reîncarci pagina curentă
  const handleCommentsChanged = useCallback(() => {
    const controller = new AbortController();
    fetchProducts(controller.signal, currentPage);
    return () => controller.abort();
  }, [fetchProducts, currentPage]);

  if (loading) {
    return (
      <div style={{ display: "grid", placeItems: "center", minHeight: 200 }}>
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return (
      <Container>
        <div style={{ color: "red", padding: "12px 0" }}>Error: {error}</div>
        <button
          className="srCom_btn"
          onClick={() => {
            const controller = new AbortController();
            fetchProducts(controller.signal, currentPage);
          }}
        >
          Retry
        </button>
      </Container>
    );
  }

  return (
    <Container>
      {productsSorted.length === 0 ? (
        <div style={{ padding: "12px 0" }}>Nu există produse.</div>
      ) : (
        <>
          {/* ✅ GRID din CarsManager */}
          <div className="srCar_grid">
            {productsSorted.map((product) => {
              const img = product.images?.[0]?.image
                ? makeImageUrl(product.images[0].image)
                : "";

              return (
                <div
                  className="srCar_card"
                  key={product.id}
                  style={{ cursor: "pointer" }}
                  onClick={() => openProduct(product)}
                >
                  <div className="srCar_cardMedia">
                    {img && <Image className="srCar_thumb" src={img} fluid />}
                  </div>
                  <div className="srCar_title">
                    {product.producator} {product.name}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ✅ Pagination (reuse classes from ProductList/CarsManager) */}
          {totalPages > 1 && (
            <nav className="srCars_pager" aria-label="Pagination">
              {currentPage > 1 && (
                <button
                  type="button"
                  className="srCars_pagerBtn"
                  onClick={() => setCurrentPage((p) => p - 1)}
                  aria-label="Previous page"
                >
                  ‹
                </button>
              )}

              <ul className="srCars_pages">
                {pageNumbers.map((n) => (
                  <li
                    key={n}
                    className={`srCars_pageNum ${currentPage === n ? "is-active" : ""}`}
                    onClick={() => setCurrentPage(n)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) =>
                      e.key === "Enter" || e.key === " " ? setCurrentPage(n) : null
                    }
                    aria-label={`Go to page ${n}`}
                  >
                    {n}
                  </li>
                ))}
              </ul>

              {currentPage < totalPages && (
                <button
                  type="button"
                  className="srCars_pagerBtn"
                  onClick={() => setCurrentPage((p) => p + 1)}
                  aria-label="Next page"
                >
                  ›
                </button>
              )}
            </nav>
          )}
        </>
      )}

      {selectedProduct && (
        <div className="srComModal_overlay" onClick={handleCloseModal}>
          <div className="srComModal_content" onClick={(e) => e.stopPropagation()}>
            <div className="srComModal_header">
              <h3 className="srComModal_heading">
                Comentarii pentru {selectedProduct.producator} {selectedProduct.name}
              </h3>

              <button
                type="button"
                className="srComModal_closeBtn"
                onClick={handleCloseModal}
                aria-label="Close"
                title="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="srComModal_body">
              <DeleteComments
                productId={selectedProduct.id}
                onChanged={() => {
                  handleCommentsChanged();
                }}
              />
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};

export default CommentsManager;